import type AwcPopoverElement from '@ambiki/impulse-view-components/dist/elements/popover';
import { Controller } from '@hotwired/stimulus';
import * as isoWeek from 'dayjs/plugin/isoWeek';
import { getDecade } from 'helpers/date';
import dayjs, { Dayjs } from 'lib/dayjs';

dayjs.extend(isoWeek);

const GRID_NUMBER = 12 as const;

export default class extends Controller {
  static targets = [
    'yearText',
    'monthBtn',
    'monthContainer',
    'yearContainer',
    'yearNavContainer',
    'monthNavContainer',
    'decadeText',
    'yearBtn',
    'yearBtnTemplate',
  ];

  private controller = new AbortController();
  static values = { datetime: String };

  activeDate = dayjs();
  yearArray: number[] = [];

  initialize() {
    this.activeDate = dayjs(this.datetimeValue);
  }

  connect(): void {
    this.popover?.addEventListener('awc-popover:hidden', this.reset.bind(this), { signal: this.controller.signal });
  }

  disconnect(): void {
    this.controller.abort();
  }

  datetimeValueChanged() {
    this.activeDate = dayjs(this.datetimeValue);

    this.publishYearChanges();
    this.initializeYearArray();
  }

  commit(event: Event) {
    const { target } = event;
    if (!(target instanceof HTMLButtonElement)) return;

    const date = normalizeDate(this.activeDate.year(), Number(target.value));
    const changeEvent = this.dispatch('change', { detail: { value: date }, bubbles: true, cancelable: true });
    if (!changeEvent) return;

    this.datetimeValue = dayjs(date).format();
  }

  updateYear(event: Event) {
    const { target } = event;
    if (!(target instanceof HTMLButtonElement)) return;

    this.activeDate = this.activeDate.year(Number(target.value));
    this.activateCurrentYear();
    this.publishYearChanges();
    this.showMonthContainer();
  }

  previousYear() {
    this.activeDate = this.activeDate.subtract(1, 'year');
    this.publishYearChanges();
  }

  nextYear() {
    this.activeDate = this.activeDate.add(1, 'year');
    this.publishYearChanges();
  }

  previousDecade() {
    const firstYearOfDecade = this.yearArray[0];
    this.yearArray = Array.from({ length: GRID_NUMBER }, (_, i) => firstYearOfDecade - GRID_NUMBER + i);
    this.populateYear();
  }

  nextDecade() {
    const lastYearOfDecade = this.yearArray[this.yearArray.length - 1];
    this.yearArray = Array.from({ length: GRID_NUMBER }, (_, i) => lastYearOfDecade + 1 + i);
    this.populateYear();
  }

  showYearContainer() {
    this.yearContainerTarget.hidden = false;
    this.yearNavContainerTarget.hidden = false;
    this.monthContainerTarget.hidden = true;
    this.monthNavContainerTarget.hidden = true;
  }

  showMonthContainer() {
    this.yearContainerTarget.hidden = true;
    this.yearNavContainerTarget.hidden = true;
    this.monthContainerTarget.hidden = false;
    this.monthNavContainerTarget.hidden = false;
  }

  reset() {
    this.activeDate = dayjs(this.datetimeValue);
    this.publishYearChanges();
    this.showMonthContainer();
    this.initializeYearArray();
  }

  private initializeYearArray() {
    const startOfYear = getDecade(dayjs(this.datetimeValue));
    this.yearArray = Array.from({ length: GRID_NUMBER }, (_, i) => startOfYear + i);
    this.populateYear();
  }

  private populateYear() {
    this.yearContainerTarget.innerHTML = '';

    this.yearArray.forEach((year) => {
      const template = this.yearBtnTemplateTarget.content.cloneNode(true);
      const button = (template as HTMLElement).querySelector('button');
      if (!button) return;

      button.innerText = year.toString();
      button.value = year.toString();
      toggleAriaCurrent(button, this.activeDate.year() === year);

      this.yearContainerTarget.append(template);
    });

    this.decadeTextTarget.innerText = `${this.yearArray[0]}-${this.yearArray[this.yearArray.length - 1]}`;
  }

  private publishYearChanges() {
    const year = this.activeDate.year();
    this.yearTextTarget.innerText = year.toString();
    this.activateCurrentMonth();
  }

  private activateCurrentYear() {
    this.yearBtnTargets.forEach((btn) => {
      const { value } = btn;
      toggleAriaCurrent(btn, this.activeDate.year() === Number(value));
    });
  }

  private activateCurrentMonth() {
    this.monthBtnTargets.forEach((btn) => {
      const date = normalizeDate(dayjs(this.datetimeValue).year(), Number(btn.value));
      toggleAriaCurrent(btn, this.activeDate.isSame(date, 'month'));
    });
  }

  private get popover() {
    return this.element.closest<AwcPopoverElement>('awc-popover');
  }

  declare readonly yearTextTarget: HTMLElement;
  declare readonly monthBtnTarget: HTMLButtonElement;
  declare readonly monthBtnTargets: HTMLButtonElement[];
  declare readonly monthContainerTarget: HTMLElement;
  declare readonly yearContainerTarget: HTMLElement;
  declare readonly monthNavContainerTarget: HTMLElement;
  declare readonly yearNavContainerTarget: HTMLElement;
  declare readonly decadeTextTarget: HTMLElement;
  declare readonly yearBtnTargets: HTMLButtonElement[];
  declare readonly yearBtnTemplateTarget: HTMLTemplateElement;
  declare datetimeValue: string;
}

function normalizeDate(year: number, month: number): Dayjs {
  let date = dayjs(new Date(year, month, 1));
  const isoDay = dayjs(date).isoWeekday();
  if (isoDay === 6) date = date.add(2, 'days');
  if (isoDay === 7) date = date.add(1, 'day');

  return date;
}

function toggleAriaCurrent(element: HTMLElement, condition: boolean) {
  if (condition) {
    element.setAttribute('aria-current', 'page');
  } else {
    element.removeAttribute('aria-current');
  }
}
